import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Wallet, CreditCard, History,
  ArrowUpRight, ArrowDownLeft, Plus,
  DollarSign, Shield, AlertCircle,
  Filter, ChevronDown, Download,
  CheckCircle, Calendar, Clock,
  CreditCardIcon, X, User, MoreHorizontal,
  BanknoteIcon, Building2, RefreshCw,
  Loader2, Lock
} from "lucide-react";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/Button";
import { Badge } from "../components/ui/badge";
import SharedHeader5 from "../Headers/SharedHeader5";
import SharedFooter3 from "../Footer/SharedFooter3";
import { PaymentMethodsProvider } from "../contexts/PaymentMethodsContext";
import { WithdrawalProvider } from "../contexts/WithdrawalContext";
import PaymentMethodsManager from "../components/payment/PaymentMethodsManager";
import StripeConnectSetup from "../components/ui/StripeConnectSetup";
import WithdrawalMethodsSetup from "../components/payment/WithdrawalMethodsSetup";
import WithdrawalForm from "../components/payment/WithdrawalForm";
import WithdrawalsList from "../components/payment/WithdrawalsList";
import AddFundsModal from "../components/payment/AddFundsModal";
import { escrowAPI, EscrowStatus, EscrowStatusEnum } from "../services/escrowAPI";
import { useSelector } from "react-redux";
import { RootState } from "../redux/store";
import { RoleEnum } from "../modules";
import { useNavigate } from "react-router-dom";
import ReceiptDialog from "../components/ui/ReceiptDialog";
import ProfileCompletionDialog from "../components/ui/ProfileCompletionDialog";
import VerificationRequirementDialog from "../components/VerificationRequirementDialog";
import { useVerification } from "../hooks/useVerification";
import { checkUserBillingInfo } from "../utils/billingUtils";
import BillingAddressRequirementDialog from "../components/ui/BillingAddressRequirementDialog";
import { getDynamicCurrencySymbol } from "../lib/currencyUtils";
import { useTranslation } from "react-i18next";

const ClientWalletPage = () => {
  const { t } = useTranslation();
  // Redux state
  const { user } = useSelector((state: RootState) => state.auth);
  const navigate = useNavigate();

  // State management
  const [activeTab, setActiveTab] = useState("transactions");
  const [filterOpen, setFilterOpen] = useState(false);
  const [addFundsModal, setAddFundsModal] = useState(false);
  const [showStripeConnect, setShowStripeConnect] = useState(false);
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [actionLoading, setActionLoading] = useState({
    addFunds: false,
    addCard: false,
    addBankAccount: false,
    withdraw: false,
    stripeConnect: false,
    refresh: false
  });
  const [showReceiptDialog, setShowReceiptDialog] = useState(false);
  const [selectedTransactionForReceipt, setSelectedTransactionForReceipt] = useState(null);
  const [showProfileCompletionDialog, setShowProfileCompletionDialog] = useState(false);
  const [showVerificationDialog, setShowVerificationDialog] = useState(false);
  const [showBillingAddressDialog, setShowBillingAddressDialog] = useState(false);
  const [pendingAddFunds, setPendingAddFunds] = useState(false);
  
  // Use verification hook
  const { isVerified, goToVerification, verificationData } = useVerification();
  
  // Track if verification data has been loaded at least once
  const [verificationDataLoaded, setVerificationDataLoaded] = useState(false);

  // Wallet data state
  const [walletData, setWalletData] = useState<{
    balance: number;
    escrowHeld: number;
    pendingPayments: number;
    availableBalance: number;
    totalSpent: number;
    currency: string;
    transactions: any[];
    totalCount: number;
    hasNextPage: boolean;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10
  });

  // Fetch wallet data
  const fetchWalletData = async () => {
    if (!user?.id) {
      setError("User not authenticated");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const data = await escrowAPI.getClientEscrowBalance(user.id, {
        page: pagination.page,
        limit: pagination.limit,
        filters: {
          escrowStatus: [EscrowStatus.PENDING, EscrowStatus.FUNDED]
        },
        sort: [{
          orderBy: "createdAt",
          order: "DESC"
        }]
      });

      setWalletData(data);
    } catch (err: any) {
      console.error("Error fetching wallet data:", err);
      setError(err?.response?.data?.message || "Failed to fetch wallet data");
    } finally {
      setLoading(false);
    }
  };

  // Load data on component mount and when pagination changes
  useEffect(() => {
    fetchWalletData();
  }, [user?.id, pagination.page, pagination.limit]);


  // Handle pagination
  const updatePagination = (newPagination: Partial<typeof pagination>) => {
    setPagination(prev => ({ ...prev, ...newPagination }));
  };

  // Refresh wallet data
  const refreshWalletData = async () => {
    setActionLoading(prev => ({ ...prev, refresh: true }));
    await fetchWalletData();
    setActionLoading(prev => ({ ...prev, refresh: false }));
  };

  // Open receipt dialog for transaction
  const openReceiptDialog = (transaction: any) => {
    if (!transaction) {
      alert("No transaction data available for receipt generation");
      return;
    }
    setSelectedTransactionForReceipt(transaction);
    setShowReceiptDialog(true);
  };

  // Check if user can proceed with action (profile + verification)
  // Check if user needs to complete profile
  const checkProfileCompletion = () => {
    if (user && !user.phone) {
      setShowProfileCompletionDialog(true);
      return false; // Profile incomplete
    }
    return true; // Profile complete
  };

  // Track when verification data has loaded
  useEffect(() => {
    if (verificationData !== null && verificationData !== undefined) {
      setVerificationDataLoaded(true);
    }
  }, [verificationData]);

  // Check if user needs verification
  const checkVerification = () => {
    // Don't check verification if data hasn't loaded yet
    if (!verificationDataLoaded) {
      return true; // Allow action to proceed while loading
    }
    
    if (!isVerified()) {
      setShowVerificationDialog(true);
      return false; // Verification incomplete
    }
    return true; // Verification complete
  };

  // Check both profile and verification
  const checkRequirements = () => {
    // Don't block actions if verification data is still loading
    if (!verificationDataLoaded) {
      // Only check profile completion while verification loads
      if (!checkProfileCompletion()) {
        return false;
      }
      return true;
    }
    
    // // Check verification first (higher priority)
    // if (!checkVerification()) {
    //   return false;
    // }
    
    // Then check profile completion
    if (!checkProfileCompletion()) {
      return false;
    }
    
    return true;
  };

  // Helper function to navigate to profile based on user role
  const navigateToProfile = () => {
    if (!user?.role?.id) return;
    
    const roleId = Number(user.role.id);
    if (roleId === RoleEnum.professional || roleId === RoleEnum.internationl_professional) {
      navigate("/myprofile");
    } else if (roleId === RoleEnum.client || roleId === RoleEnum.internationl_client) {
      navigate("/client/profile");
    } else if (roleId === RoleEnum.admin) {
      navigate("/admin/dashboard");
    }
    setShowProfileCompletionDialog(false);
  };

  // Handle billing saved callback
  const handleBillingSaved = () => {
    // Check if user now has billing information
    const billingCheck = checkUserBillingInfo(user);
    
    // After billing info is saved, check and open add funds if pending
    if (billingCheck.hasBillingInfo && pendingAddFunds) {
      setAddFundsModal(true);
      setPendingAddFunds(false);
    }
  };

  // Helper function for currency formatting
  const formatCurrency = (amount: number) => {
    const formattedAmount = amount.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    return `${getDynamicCurrencySymbol()}${formattedAmount}`;
  };

  // Helper function to get status display info
  const getStatusInfo = (statusId: number) => {
    switch (statusId) {
      case EscrowStatusEnum.pending:
        return {
          label: "Pending",
          color: "text-yellow-600",
          bgColor: "bg-yellow-100",
          badgeClass: "bg-yellow-100 text-yellow-800",
          iconBg: "bg-yellow-100",
          iconColor: "text-yellow-600"
        };
      case EscrowStatusEnum.funded:
        return {
          label: "Funded",
          color: "text-blue-600",
          bgColor: "bg-blue-100",
          badgeClass: "bg-blue-100 text-blue-800",
          iconBg: "bg-blue-100",
          iconColor: "text-blue-600"
        };
      case EscrowStatusEnum.released:
        return {
          label: "Released",
          color: "text-green-600",
          bgColor: "bg-green-100",
          badgeClass: "bg-green-100 text-green-800",
          iconBg: "bg-green-100",
          iconColor: "text-green-600"
        };
      case EscrowStatusEnum.disputed:
        return {
          label: "Disputed",
          color: "text-orange-600",
          bgColor: "bg-orange-100",
          badgeClass: "bg-orange-100 text-orange-800",
          iconBg: "bg-orange-100",
          iconColor: "text-orange-600"
        };
      case EscrowStatusEnum.resolved:
        return {
          label: "Resolved",
          color: "text-teal-600",
          bgColor: "bg-teal-100",
          badgeClass: "bg-teal-100 text-teal-800",
          iconBg: "bg-teal-100",
          iconColor: "text-teal-600"
        };
      case EscrowStatusEnum.refunded:
        return {
          label: "Refunded",
          color: "text-green-600",
          bgColor: "bg-green-100",
          badgeClass: "bg-green-100 text-green-800",
          iconBg: "bg-green-100",
          iconColor: "text-green-600"
        };
      case EscrowStatusEnum.cancelled:
        return {
          label: "Cancelled",
          color: "text-gray-600",
          bgColor: "bg-gray-100",
          badgeClass: "bg-gray-100 text-gray-800",
          iconBg: "bg-gray-100",
          iconColor: "text-gray-600"
        };
      case EscrowStatusEnum.failed:
        return {
          label: "Failed",
          color: "text-red-700",
          bgColor: "bg-red-100",
          badgeClass: "bg-red-100 text-red-800",
          iconBg: "bg-red-100",
          iconColor: "text-red-700"
        };
      case EscrowStatusEnum.withdrawn:
        return {
          label: "Withdrawn",
          color: "text-purple-600",
          bgColor: "bg-purple-100",
          badgeClass: "bg-purple-100 text-purple-800",
          iconBg: "bg-purple-100",
          iconColor: "text-purple-600"
        };
      case EscrowStatusEnum.request_withdraw:
        return {
          label: "Withdrawal Requested",
          color: "text-indigo-600",
          bgColor: "bg-indigo-100",
          badgeClass: "bg-indigo-100 text-indigo-800",
          iconBg: "bg-indigo-100",
          iconColor: "text-indigo-600"
        };
      case EscrowStatusEnum.user_funded:
        return {
          label: "Funds Added",
          color: "text-emerald-600",
          bgColor: "bg-emerald-100",
          badgeClass: "bg-emerald-100 text-emerald-800",
          iconBg: "bg-emerald-100",
          iconColor: "text-emerald-600"
        };
      case EscrowStatusEnum.credits_purchased:
        return {
          label: "Credits Purchased",
          color: "text-purple-600",
          bgColor: "bg-purple-100",
          badgeClass: "bg-purple-100 text-purple-800",
          iconBg: "bg-purple-100",
          iconColor: "text-purple-600"
        };
      case EscrowStatusEnum.quote_purchased:
        return {
          label: "Quote Purchased",
          color: "text-indigo-600",
          bgColor: "bg-indigo-100",
          badgeClass: "bg-indigo-100 text-indigo-800",
          iconBg: "bg-indigo-100",
          iconColor: "text-indigo-600"
        };
      case EscrowStatusEnum.used_credit_purchased:
        return {
          label: "Credit Used",
          color: "text-purple-700",
          bgColor: "bg-purple-100",
          badgeClass: "bg-purple-100 text-purple-800",
          iconBg: "bg-purple-100",
          iconColor: "text-purple-700"
        };
      case EscrowStatusEnum.used_quote_purchased:
        return {
          label: "Quote Used",
          color: "text-indigo-700",
          bgColor: "bg-indigo-100",
          badgeClass: "bg-indigo-100 text-indigo-800",
          iconBg: "bg-indigo-100",
          iconColor: "text-indigo-700"
        };
      default:
        return {
          label: "Unknown",
          color: "text-gray-600",
          bgColor: "bg-gray-100",
          badgeClass: "bg-gray-100 text-gray-800",
          iconBg: "bg-gray-100",
          iconColor: "text-gray-600"
        };
    }
  };

  // Check if user has Stripe Connect setup (only required for professionals)
  const hasStripeConnect = (user as any)?.stripeConnectedAccountId;
  const isClient = Number(user?.role?.id) === RoleEnum.client;
  const needsStripeSetup = !hasStripeConnect && !isClient;

  // Transaction modal component
  const TransactionModal = ({ transaction, onClose }) => {
    if (!transaction) return null;

    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
      >
        <motion.div
          initial={{ scale: 0.95 }}
          animate={{ scale: 1 }}
          exit={{ scale: 0.95 }}
          className="bg-white rounded-xl max-w-lg w-full p-6"
        >
          <div className="flex justify-between items-start mb-6">
            <div>
              <h2 className="text-xl font-bold text-gray-900">{t("clientWalletPage.transactionDetails")}</h2>
              <p className="text-sm text-gray-600">ID: {transaction.id}</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <X className="h-6 w-6 text-gray-500" />
            </button>
          </div>

          <div className="space-y-4">
            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-600">{t("clientWalletPage.totalAmount")}</span>
                <span className="text-xl font-bold text-gray-900">
                  {formatCurrency(transaction.totalAmount || transaction.amount || 0)}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">{t("clientWalletPage.baseAmount")}</span>
                <span className="text-gray-900">
                  {formatCurrency(transaction.withdrawlAmount || 0)}
                </span>
              </div>
              {transaction.emergencyCost && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">{t("clientWalletPage.emergencyCost")}</span>
                  <span className="text-gray-900">
                    {formatCurrency(transaction.emergencyCost)}
                  </span>
                </div>
              )}
              {transaction.trackerAmount && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">{t("clientWalletPage.trackerAmount")}</span>
                  <span className="text-gray-900">
                    {formatCurrency(transaction.trackerAmount)}
                  </span>
                </div>
              )}
              {transaction.platformFee && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">{t("clientWalletPage.platformFee")}</span>
                  <span className="text-gray-900">
                    {formatCurrency(transaction.platformFee)}
                  </span>
                </div>
              )}
            </div>

            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">{t("clientWalletPage.type")}</span>
                <span className="font-medium capitalize">{t("clientWalletPage.payment")}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">{t("clientWalletPage.status")}</span>
                <Badge variant={undefined} className={getStatusInfo(Number(transaction.status?.id) || 0).badgeClass}>
                  {getStatusInfo(Number(transaction.status?.id) || 0).label}
                </Badge>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">{t("clientWalletPage.created")}</span>
                <span className="font-medium">
                  {new Date(transaction.createdAt).toLocaleString()}
                </span>
              </div>
              {transaction.expectedReleaseDate && (
                <div className="flex justify-between">
                  <span className="text-gray-600">{t("clientWalletPage.expectedRelease")}</span>
                  <span className="font-medium">
                    {new Date(transaction.expectedReleaseDate).toLocaleString()}
                  </span>
                </div>
              )}
              {transaction.actualReleaseDate && (
                <div className="flex justify-between">
                  <span className="text-gray-600">{t("clientWalletPage.released")}</span>
                  <span className="font-medium">
                    {new Date(transaction.actualReleaseDate).toLocaleString()}
                  </span>
                </div>
              )}
              {transaction.currency && (
                <div className="flex justify-between">
                  <span className="text-gray-600">{t("clientWalletPage.currency")}</span>
                  <span className="font-medium">{transaction.currency}</span>
                </div>
              )}
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">{t("clientWalletPage.description")}</h3>
              <p className="text-gray-600">
                {transaction.milestone?.title || transaction.notes || t("clientWalletPage.paymentForMilestone")}
              </p>
            </div>

            {(Number(transaction.status?.id) === EscrowStatusEnum.funded || Number(transaction.status?.id) === EscrowStatusEnum.user_funded) && (
              <div className="p-4 bg-blue-50 rounded-lg">
                <div className="flex items-start gap-3">
                  <Lock className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-blue-600">{t("clientWalletPage.fundsInEscrow")}</p>
                    {transaction.expectedReleaseDate && (
                      <p className="text-sm text-blue-500">
                        {t("clientWalletPage.willBeReleasedOn", { date: new Date(transaction.expectedReleaseDate).toLocaleDateString() })}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {Number(transaction.status?.id) === EscrowStatusEnum.disputed && (
              <div className="p-4 bg-orange-50 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-orange-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-orange-600">{t("clientWalletPage.paymentUnderDispute")}</p>
                    {transaction.disputeDescription && (
                      <p className="text-sm text-orange-500 mt-1">
                        {transaction.disputeDescription}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {Number(transaction.status?.id) === EscrowStatusEnum.request_withdraw && (
              <div className="p-4 bg-indigo-50 rounded-lg">
                <div className="flex items-start gap-3">
                  <Clock className="h-5 w-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-indigo-600">{t("clientWalletPage.withdrawalRequestPending")}</p>
                    <p className="text-sm text-indigo-500">
                      {t("clientWalletPage.awaitingWithdrawalApproval")}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => openReceiptDialog(transaction)}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50 flex items-center gap-2"
              >
                <Download className="h-4 w-4" />
                {t("clientWalletPage.downloadReceipt")}
              </button>
              {(Number(transaction.status?.id) === EscrowStatusEnum.funded || Number(transaction.status?.id) === EscrowStatusEnum.user_funded) && (
                <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                  {t("clientWalletPage.viewProject")}
                </button>
              )}
              {transaction.contract && (
                <button className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
                  {t("clientWalletPage.viewContract")}
                </button>
              )}
            </div>
          </div>
        </motion.div>
      </motion.div>
    );
  };

  // Transaction Card Component
  const TransactionCard = ({ transaction }) => {
    const statusInfo = getStatusInfo(Number(transaction.status?.id) || 0);
    const isReleased = Number(transaction.status?.id) === EscrowStatusEnum.released;
    const isUserFunded = Number(transaction.status?.id) === EscrowStatusEnum.user_funded;
    const isFunded = Number(transaction.status?.id) === EscrowStatusEnum.funded;
    const isWithdrawn = Number(transaction.status?.id) === EscrowStatusEnum.withdrawn;
    const isRefunded = Number(transaction.status?.id) === EscrowStatusEnum.refunded;
    const isDisputed = Number(transaction.status?.id) === EscrowStatusEnum.disputed;
    const isPending = Number(transaction.status?.id) === EscrowStatusEnum.pending;

    return (
      <div
        className="p-3 md:p-4 border-b last:border-b-0 hover:bg-gray-50 transition-colors cursor-pointer"
      >
        <div className="flex flex-col md:flex-row items-start justify-between gap-3 md:gap-4">
          <div className="flex items-start gap-3 w-full md:w-auto">
            <div className={`p-2 rounded-lg ${statusInfo.iconBg}`}>
              {isReleased ? (
                <ArrowDownLeft className={`h-4 w-4 md:h-5 md:w-5 ${statusInfo.iconColor}`} />
              ) : isRefunded ? (
                <ArrowDownLeft className={`h-4 w-4 md:h-5 md:w-5 ${statusInfo.iconColor}`} />
              ) : isWithdrawn ? (
                <Download className={`h-4 w-4 md:h-5 md:w-5 ${statusInfo.iconColor}`} />
              ) : isUserFunded ? (
                <Plus className={`h-4 w-4 md:h-5 md:w-5 ${statusInfo.iconColor}`} />
              ) : isFunded ? (
                <Lock className={`h-4 w-4 md:h-5 md:w-5 ${statusInfo.iconColor}`} />
              ) : isDisputed ? (
                <AlertCircle className={`h-4 w-4 md:h-5 md:w-5 ${statusInfo.iconColor}`} />
              ) : isPending ? (
                <Clock className={`h-4 w-4 md:h-5 md:w-5 ${statusInfo.iconColor}`} />
              ) : (
                <Lock className={`h-4 w-4 md:h-5 md:w-5 ${statusInfo.iconColor}`} />
              )}
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-2 md:gap-4">
                <div>
                  <h4 className="font-medium text-gray-900 text-sm md:text-base truncate">
                    {transaction.milestone?.title || transaction.notes || "Payment"}
                  </h4>
                  <div className="flex flex-wrap items-center gap-2 text-xs md:text-sm text-gray-600 mt-1">
                    <Calendar className="h-3 w-3 md:h-4 md:w-4" />
                    {new Date(transaction.createdAt).toLocaleDateString()}
                    <span>•</span>
                    <span className="truncate">{transaction.id}</span>
                    <span>•</span>
                    <Badge variant={undefined} className={`text-xs md:text-sm px-2 py-0.5 ${statusInfo.badgeClass}`}>
                      {statusInfo.label}
                    </Badge>
                  </div>
                </div>
                <div className="text-right w-full md:w-auto">
                  <div className={`font-medium text-sm md:text-base ${
                    (isUserFunded || isRefunded) ? "text-green-600" : 
                    (isReleased || isFunded) ? "text-red-600" : 
                    statusInfo.color
                  }`}>
                    {(isUserFunded || isRefunded) ? "+" : (isReleased || isFunded) ? "-" : "+"}
                    {formatCurrency((transaction.amount || 0)+ (transaction.trackerAmount || 0)+ (transaction.emergencyCost || 0))}
                  </div>
                  {transaction.platformFee && (
                    <div className="text-xs md:text-sm text-gray-500">
                      Fee: {formatCurrency(transaction.platformFee)}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2 mt-3">
            <button
              onClick={(e) => {
                e.stopPropagation();
                openReceiptDialog(transaction);
              }}
              className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
            >
              <Download className="h-3 w-3" />
              {t("clientWalletPage.downloadReceipt")}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Payment Method Card Component
  const PaymentMethodCard = ({ method }) => {
    const isCard = method.type === "card";

    return (
      <Card className={`p-3 md:p-4 ${method.isDefault ? "border-blue-200" : ""}`}>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2 md:gap-3">
            {isCard ? (
              <CreditCardIcon className="h-6 w-6 md:h-8 md:w-8 text-gray-600" />
            ) : (
              <BanknoteIcon className="h-6 w-6 md:h-8 md:w-8 text-gray-600" />
            )}
            <div>
              <div className="flex items-center gap-2">
                <h4 className="font-medium text-sm md:text-base">
                  {isCard ? (
                    `${method.cardBrand.toUpperCase()} **** ${method.last4}`
                  ) : (
                    `${method.bankName} **** ${method.last4}`
                  )}
                </h4>
                {method.isDefault && (
                  <Badge variant={undefined} className="text-xs md:text-sm bg-blue-100 text-blue-800">Default</Badge>
                )}
              </div>
              <p className="text-xs md:text-sm text-gray-600">
                {isCard ? (
                  `Expires ${method.expMonth}/${method.expYear}`
                ) : (
                  `${method.accountType.charAt(0).toUpperCase() + method.accountType.slice(1)} Account`
                )}
              </p>
            </div>
          </div>
          <Button variant="ghost" size="sm" className="p-1 md:p-2">
            <MoreHorizontal className="h-4 w-4 md:h-5 md:w-5" />
          </Button>
        </div>
      </Card>
    );
  };


  // Loading state
  if (loading && !walletData) {
    return (
      <PaymentMethodsProvider>
        <WithdrawalProvider>
          <div className="min-h-screen bg-gray-50 flex flex-col" style={{ textAlign: "left" }}>
            <SharedHeader5 />
            <main className="flex-1 max-w-7xl mx-auto px-4 py-6 md:py-8 w-full">
              <div className="flex items-center justify-center min-h-[400px]">
                <div className="text-center">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
                  <p className="text-gray-600">{t("clientWalletPage.loadingWalletData")}</p>
                </div>
              </div>
            </main>
            <SharedFooter3 />
          </div>
        </WithdrawalProvider>
      </PaymentMethodsProvider>
    );
  }

  // Error state
  if (error && !walletData) {
    return (
      <PaymentMethodsProvider>
        <WithdrawalProvider>
          <div className="min-h-screen bg-gray-50 flex flex-col" style={{ textAlign: "left" }}>
            <SharedHeader5 />
            <main className="flex-1 max-w-7xl mx-auto px-4 py-6 md:py-8 w-full">
              <div className="flex items-center justify-center min-h-[400px]">
                <div className="text-center">
                  <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">{t("clientWalletPage.errorLoadingWallet")}</h3>
                  <p className="text-gray-600 mb-4">{error}</p>
                  <Button
                    onClick={refreshWalletData}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 mx-auto"
                  >
                    <RefreshCw className="h-4 w-4" />
                    {t("clientWalletPage.tryAgain")}
                  </Button>
                </div>
              </div>
            </main>
            <SharedFooter3 />
          </div>
        </WithdrawalProvider>
      </PaymentMethodsProvider>
    );
  }

  return (
    <PaymentMethodsProvider>
      <WithdrawalProvider>
        <div className="min-h-screen bg-gray-50 flex flex-col" style={{ textAlign: "left" }}>
          <SharedHeader5 />

          <main className="flex-1 max-w-7xl mx-auto px-4 py-6 md:py-8 w-full">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6 md:mb-8">
              <div>
                <h1 className="text-xl md:text-2xl font-bold text-gray-900">{t("clientWalletPage.title")}</h1>
                <p className="text-sm md:text-base text-gray-600">{t("clientWalletPage.subtitle")}</p>
              </div>
            </div>

            {/* Wallet Balance Card */}
            <Card className="mb-6">
              <div className="p-4 md:p-6 border-b">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                  <div>
                    <div className="text-sm text-gray-600 mb-1">{t("clientWalletPage.availableBalance")}</div>
                    <div className="text-2xl md:text-3xl font-bold text-gray-900">
                      {formatCurrency(walletData?.availableBalance || 0)}
                    </div>
                    <div className="text-xs md:text-sm text-gray-500 mt-1">
                      {t("clientWalletPage.lastUpdated")} {walletData ? new Date().toLocaleString() : "Loading..."}
                    </div>
                  </div>
                  {(walletData?.escrowHeld && walletData.escrowHeld > 0) && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                      <div className="text-xs md:text-sm text-yellow-800">{t("clientWalletPage.onHold")}</div>
                      <div className="text-sm md:text-base font-medium text-yellow-900">
                        {formatCurrency(walletData.escrowHeld)}
                      </div>
                    </div>
                  )}
                  <div className="flex gap-2 md:gap-3 w-full md:w-auto">
                    <Button
                      onClick={() => {
                        // Check if user needs to complete profile and verification
                        if (!checkRequirements()) {
                          return;
                        }

                        // Check if user has billing information for adding funds
                        const billingCheck = checkUserBillingInfo(user);
                        
                        if (!billingCheck.hasBillingInfo) {
                          // Show billing address requirement dialog and mark add funds as pending
                          setPendingAddFunds(true);
                          setShowBillingAddressDialog(true);
                          return;
                        }

                        // User has billing info, proceed with adding funds
                        setAddFundsModal(true);
                      }}
                      disabled={actionLoading.addFunds}
                      className="flex items-center gap-2 text-sm md:text-base w-full md:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {actionLoading.addFunds ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Plus className="h-4 w-4" />
                      )}
                      {actionLoading.addFunds ? t("clientWalletPage.processing") : t("clientWalletPage.addFunds")}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x">
                <div className="p-4 md:p-6">
                  <div className="text-xs md:text-sm text-gray-600">{t("clientWalletPage.totalSpent")}</div>
                  <div className="text-lg md:text-xl font-semibold mt-1">
                    {formatCurrency(walletData?.totalSpent || 0)}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">{t("clientWalletPage.fundedReleased")}</div>
                </div>
                <div className="p-4 md:p-6">
                  <div className="text-xs md:text-sm text-gray-600">{t("clientWalletPage.onHold")}</div>
                  <div className="text-lg md:text-xl font-semibold mt-1">
                    {formatCurrency(walletData?.escrowHeld || 0)}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">{t("clientWalletPage.inEscrow")}</div>
                </div>
                <div className="p-4 md:p-6">
                  <div className="text-xs md:text-sm text-gray-600">{t("clientWalletPage.pendingPayments")}</div>
                  <div className="text-lg md:text-xl font-semibold mt-1">
                    {formatCurrency(walletData?.pendingPayments || 0)}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">{t("clientWalletPage.awaitingAction")}</div>
                </div>
              </div>
            </Card>

            {/* Main Content */}
            <div className="space-y-6">
              {/* Tabs */}
              <div className="flex items-center gap-4 border-b overflow-x-auto">
                <button
                  onClick={() => setActiveTab("transactions")}
                  className={`px-3 md:px-4 py-2 border-b-2 text-xs md:text-sm font-medium whitespace-nowrap ${activeTab === "transactions"
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                    }`}
                >
                  {t("clientWalletPage.transactions")}
                </button>
                <button
                  onClick={() => setActiveTab("payment-methods")}
                  className={`px-3 md:px-4 py-2 border-b-2 text-xs md:text-sm font-medium whitespace-nowrap ${activeTab === "payment-methods"
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                    }`}
                >
                  {t("clientWalletPage.paymentMethods")}
                </button>
                {/* <button
                   onClick={() => setActiveTab("withdrawals")}
                   className={`px-3 md:px-4 py-2 border-b-2 text-xs md:text-sm font-medium whitespace-nowrap ${activeTab === "withdrawals"
                     ? "border-blue-500 text-blue-600"
                     : "border-transparent text-gray-500 hover:text-gray-700"
                     }`}
                 >
                   Withdrawals
                 </button> */}
                {/* <button
                   onClick={() => setActiveTab("stripe-connect")}
                   className={`px-3 md:px-4 py-2 border-b-2 text-xs md:text-sm font-medium whitespace-nowrap ${activeTab === "stripe-connect"
                     ? "border-blue-500 text-blue-600"
                     : "border-transparent text-gray-500 hover:text-gray-700"
                     }`}
                 >
                   Stripe Connect
                 </button> */}
              </div>

              {/* Content */}
              {activeTab === "transactions" ? (
                <div>
                  {/* Transactions List */}
                  <Card>
                    {/* Header with filters */}
                    <div className="p-4 border-b">
                      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                        <h3 className="font-semibold text-sm md:text-base">{t("clientWalletPage.transactionHistory")}</h3>
                        <div className="flex flex-wrap items-center gap-2 md:gap-3 w-full sm:w-auto">
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex items-center gap-2 text-xs md:text-sm w-full sm:w-auto"
                            onClick={() => setFilterOpen(!filterOpen)}
                          >
                            <Filter className="h-3 w-3 md:h-4 md:w-4" />
                            {t("clientWalletPage.filter")}
                            <ChevronDown className="h-3 w-3 md:h-4 md:w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={actionLoading.refresh}
                            className="flex items-center gap-2 text-xs md:text-sm w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                            onClick={refreshWalletData}
                          >
                            {actionLoading.refresh ? (
                              <Loader2 className="h-3 w-3 md:h-4 md:w-4 animate-spin" />
                            ) : (
                              <RefreshCw className="h-3 w-3 md:h-4 md:w-4" />
                            )}
                            {actionLoading.refresh ? t("clientWalletPage.refreshing") : t("clientWalletPage.refresh")}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex items-center gap-2 text-xs md:text-sm w-full sm:w-auto"
                          >
                            <Download className="h-3 w-3 md:h-4 md:w-4" />
                            {t("clientWalletPage.export")}
                          </Button>
                        </div>
                      </div>

                      {/* Filter Panel */}
                      {filterOpen && (
                        <div className="mt-4 pt-4 border-t">
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                            <div>
                              <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                                {t("clientWalletPage.type")}
                              </label>
                              <select className="w-full p-2 border rounded-lg text-xs md:text-sm">
                                <option value="all">{t("clientWalletPage.allTypes")}</option>
                                <option value="payment">{t("clientWalletPage.payments")}</option>
                                <option value="deposit">{t("clientWalletPage.deposits")}</option>
                                <option value="hold">{t("clientWalletPage.holds")}</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                                {t("clientWalletPage.status")}
                              </label>
                              <select className="w-full p-2 border rounded-lg text-xs md:text-sm">
                                <option value="all">{t("clientWalletPage.allStatus")}</option>
                                <option value="completed">{t("clientWalletPage.completed")}</option>
                                <option value="pending">{t("clientWalletPage.pending")}</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                                {t("clientWalletPage.dateRange")}
                              </label>
                              <select className="w-full p-2 border rounded-lg text-xs md:text-sm">
                                <option value="all">{t("clientWalletPage.allTime")}</option>
                                <option value="today">{t("clientWalletPage.today")}</option>
                                <option value="this-week">{t("clientWalletPage.thisWeek")}</option>
                                <option value="this-month">{t("clientWalletPage.thisMonth")}</option>
                                <option value="last-3-months">{t("clientWalletPage.last3Months")}</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                                {t("clientWalletPage.amountRange")}
                              </label>
                              <select className="w-full p-2 border rounded-lg text-xs md:text-sm">
                                <option value="all">{t("clientWalletPage.allAmounts")}</option>
                                <option value="under-100">Under {getDynamicCurrencySymbol()}100</option>
                                <option value="100-500">{getDynamicCurrencySymbol()}100 - {getDynamicCurrencySymbol()}500</option>
                                <option value="over-500">Over {getDynamicCurrencySymbol()}500</option>
                              </select>
                            </div>
                          </div>
                          <div className="flex justify-end mt-4">
                            <Button variant="outline" className="mr-2 text-xs md:text-sm">
                              {t("clientWalletPage.reset")}
                            </Button>
                            <Button className="text-xs md:text-sm">{t("clientWalletPage.apply")}</Button>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Transactions List */}
                    <div className="divide-y">
                      {loading && !walletData?.transactions?.length ? (
                        <div className="p-6 text-center">
                          <Loader2 className="h-6 w-6 animate-spin text-blue-600 mx-auto mb-2" />
                          <p className="text-gray-600">{t("clientWalletPage.loadingTransactions")}</p>
                        </div>
                      ) : error ? (
                        <div className="p-6 text-center">
                          <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
                          <h3 className="text-lg font-medium text-gray-900 mb-2">{t("clientWalletPage.errorLoadingWallet")}</h3>
                          <p className="text-gray-600 mb-4">{error}</p>
                          <Button
                            onClick={refreshWalletData}
                            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 mx-auto"
                          >
                            <RefreshCw className="h-4 w-4" />
                            {t("clientWalletPage.tryAgain")}
                          </Button>
                        </div>
                      ) : walletData?.transactions?.filter(t => Number(t.status?.id) !== EscrowStatus.USED_FUNDS).length === 0 ? (
                        <div className="p-4 md:p-6 text-center">
                          <History className="h-8 w-8 md:h-12 md:w-12 text-gray-400 mx-auto mb-4" />
                          <h3 className="text-base md:text-lg font-medium text-gray-900 mb-2">
                            {t("clientWalletPage.noTransactions")}
                          </h3>
                          <p className="text-sm md:text-base text-gray-600">
                            {t("clientWalletPage.noTransactionsMessage")}
                          </p>
                        </div>
                      ) : (
                        walletData?.transactions
                          ?.filter(transaction => Number(transaction.status?.id) !== EscrowStatus.USED_FUNDS)
                          .map((transaction) => (
                            <TransactionCard
                              key={transaction.id}
                              transaction={transaction}
                            />
                          ))
                      )}
                    </div>

                    {/* Pagination */}
                    {walletData?.transactions?.filter(t => Number(t.status?.id) !== EscrowStatusEnum.user_funded).length > 0 && (
                      <div className="p-4 border-t">
                        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                          <div className="text-xs md:text-sm text-gray-600 w-full sm:w-auto text-center sm:text-left">
                            {t("clientWalletPage.showing", { count: walletData.transactions.filter(t => Number(t.status?.id) !== EscrowStatusEnum.user_funded).length })}
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-xs md:text-sm"
                              onClick={() => updatePagination({ page: Math.max(1, pagination.page - 1) })}
                              disabled={pagination.page === 1 || loading}
                            >
                              {t("clientWalletPage.previous")}
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-xs md:text-sm"
                              onClick={() => updatePagination({ page: pagination.page + 1 })}
                              disabled={!walletData.hasNextPage || loading}
                            >
                              {t("clientWalletPage.next")}
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}
                  </Card>
                </div>
              ) : activeTab === "withdrawals" ? (
                <div>
                  <Card className="p-4 md:p-6">
                    <div className="space-y-6">
                      {/* Header with Request Withdrawal Button */}
                      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                        <div>
                          <h3 className="text-lg font-medium text-gray-900">{t("clientWalletPage.withdrawals")}</h3>
                          <p className="text-sm text-gray-600">{t("clientWalletPage.viewAndManage")}</p>
                        </div>
                        <Button
                          onClick={() => setShowWithdraw(true)}
                          disabled={actionLoading.withdraw}
                          className="flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {actionLoading.withdraw ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Download className="h-4 w-4" />
                          )}
                          {actionLoading.withdraw ? t("clientWalletPage.processing") : t("clientWalletPage.requestWithdrawal")}
                        </Button>
                      </div>

                      {/* Withdrawals List */}
                      <WithdrawalsList />
                    </div>
                  </Card>
                </div>
              ) : activeTab === "stripe-connect" ? (
                <div>
                  <Card className="p-4 md:p-6">
                    <StripeConnectSetup
                      onComplete={() => {
                        // Optionally show a success message or update UI state
                        console.log("Stripe Connect setup completed successfully");
                      }}
                      onError={(error) => {
                        console.error("Stripe Connect setup error:", error);
                      }}
                    />
                  </Card>
                </div>
              ) : (
                <div>
                  {needsStripeSetup ? (
                    <div className="space-y-6">
                      {/* Stripe Connect Setup Required */}
                      <Card className="p-6">
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                          <div className="flex items-start gap-4">
                            <div className="p-2 bg-yellow-100 rounded-lg">
                              <AlertCircle className="h-6 w-6 text-yellow-600" />
                            </div>
                            <div className="flex-1">
                              <h3 className="text-lg font-semibold text-yellow-800 mb-2">
                                {t("clientWalletPage.paymentSetupRequired")}
                              </h3>
                              <p className="text-yellow-700 mb-4">
                                {t("clientWalletPage.setupMessage")}
                              </p>
                              <button
                                onClick={() => setShowStripeConnect(true)}
                                className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors"
                              >
                                {t("clientWalletPage.setupWithdrawalMethods")}
                              </button>
                            </div>
                          </div>
                        </div>
                      </Card>
                    </div>
                  ) : (
                    <PaymentMethodsManager availableBalance={undefined} />
                  )}
                </div>
              )}
            </div>
          </main>

          {/* Modals */}
          <AnimatePresence>
            {selectedTransaction && (
              <TransactionModal
                transaction={selectedTransaction}
                onClose={() => setSelectedTransaction(null)}
              />
            )}
          </AnimatePresence>

          <AddFundsModal
            isOpen={addFundsModal}
            onClose={() => {
              setAddFundsModal(false);
              setActionLoading(prev => ({ ...prev, addFunds: false }));
            }}
            onSuccess={async () => {
              setActionLoading(prev => ({ ...prev, addFunds: true }));
              setAddFundsModal(false);
              await refreshWalletData(); // Refresh wallet data after successful funding
              setActionLoading(prev => ({ ...prev, addFunds: false }));
            }}
          />

          {/* Withdrawal Form Modal */}
          <AnimatePresence>
            {showWithdraw && (
              <WithdrawalForm
                onClose={() => {
                  setShowWithdraw(false);
                  setActionLoading(prev => ({ ...prev, withdraw: false }));
                }}
                availableBalance={walletData?.availableBalance}
              />
            )}
          </AnimatePresence>

          {/* Withdrawal Methods Setup Modal */}
          {showStripeConnect && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg p-4 md:p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-base md:text-lg font-semibold">{t("clientWalletPage.setupWithdrawalMethods")}</h3>
                  <button
                    onClick={() => {
                      setShowStripeConnect(false);
                      setActionLoading(prev => ({ ...prev, stripeConnect: false }));
                    }}
                    className="p-1"
                    disabled={actionLoading.stripeConnect}
                  >
                    <X className="h-4 w-4 md:h-5 md:w-5" />
                  </button>
                </div>
                <WithdrawalMethodsSetup
                  onComplete={async () => {
                    setActionLoading(prev => ({ ...prev, stripeConnect: true }));
                    setShowStripeConnect(false);
                    await refreshWalletData(); // Refresh wallet data after successful setup
                    setActionLoading(prev => ({ ...prev, stripeConnect: false }));
                    console.log("Withdrawal methods setup completed successfully");
                  }}
                  onError={(error) => {
                    setActionLoading(prev => ({ ...prev, stripeConnect: false }));
                    console.error("Withdrawal methods setup error:", error);
                  }}
                />
              </div>
            </div>
          )}

          {/* Receipt Dialog */}
          <ReceiptDialog
            isOpen={showReceiptDialog}
            onClose={() => {
              setShowReceiptDialog(false);
              setSelectedTransactionForReceipt(null);
            }}
            transaction={selectedTransactionForReceipt}
          />

          {/* Profile and Verification Dialog */}
          {/* Profile Completion Dialog */}
          <ProfileCompletionDialog
            isOpen={showProfileCompletionDialog}
            onClose={() => setShowProfileCompletionDialog(false)}
            onGoToProfile={navigateToProfile}
            userRole={Number(user?.role?.id)}
          />

          {/* Verification Requirement Dialog */}
          <VerificationRequirementDialog
            isOpen={showVerificationDialog}
            onClose={() => setShowVerificationDialog(false)}
            title="Verification Required"
            message="Please complete your account verification to add funds to your wallet."
          />

          {/* Billing Address Requirement Dialog */}
          <BillingAddressRequirementDialog
            isOpen={showBillingAddressDialog}
            onClose={() => {
              setShowBillingAddressDialog(false);
              setPendingAddFunds(false);
            }}
            onBillingSaved={handleBillingSaved}
            userRole={Number(user?.role?.id)}
            action="payment"
          />

          {/* Footer */}
          <SharedFooter3 />
        </div>
      </WithdrawalProvider>
    </PaymentMethodsProvider>
  );
};

export default ClientWalletPage;